import React, { useMemo } from 'react';
import UserContext from 'common/utils/userContext';
import Mobile from 'components/Mobile/Mobile';
import Desktop from 'components/Desktop/Desktop';
import MobileHeader from 'components/MobileHeader/MobileHeader';
import DesktopHeader from 'components/DesktopHeader/DesktopHeader';
import Footer from 'components/Footer/Footer';
import { getMenu, getSubMenu } from 'common/helpers/content';
import { IUser } from 'features/core/types';
import AccountSubmenu from 'components/AccountSubmenu/AccountSubmenu';
import { useLocation, useParams } from 'react-router-dom';
import { ENV_VAR } from 'app/api';
import { getSearchWithoutLocale } from 'common/helpers/resolvePath';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectPage } from 'features/content/selectors';
import { CustomerStatus } from 'common/consts/customerStatus';

interface Props {
  settings: any;
  localePath: string;
  masterLanguage: string;
  user: IUser | null;
  children: React.ReactNode;
}

const BaseLayout = ({
  settings,
  localePath,
  masterLanguage,
  user,
  children,
}: Props) => {
  const menuLinks = useMemo(() => {
    return getMenu(settings?.MainMenu);
  }, [settings]);

  const myAccountSubMenuLinks = useMemo(() => {
    return getSubMenu(settings?.MyAccountSubMenus);
  }, [settings]);
  const footerSettings = useMemo(() => {
    return settings?.PageData?.SiteSettings?.FooterSettings;
  }, [settings]);

  const SiteSetingsRegisterLogin = useMemo(() => ({
    registrationPage: settings?.PageData?.SiteSettings?.RegisterYourAccountPage,
    loginPage: settings?.PageData?.SiteSettings?.PortalPage,
  }), [settings])

  const appLinks = useMemo(
    () => settings?.LayoutModel?.SiteNavigationService,
    [settings],
  );
  const showIncompleteDetailsAlert = useMemo(
    () => settings?.LayoutModel?.ShowIncompleteDetailsAlert,
    [settings],
  );
  const registration = useMemo(
    () => ({
      url: appLinks?.VerifyIdentityPage?.Uri,
      text: settings?.PageData?.SiteSettings?.RegisterYourAccountButtonLabel,
    }),
    [settings],
  );
  const instantPayment = useMemo(
    () => ({
      url: appLinks?.DirectPaymentPage.Uri,
      text: settings?.PageData?.SiteSettings?.MakeAnInstantPaymentButtonLabel,
    }),
    [settings],
  );
  const translations = useMemo(
    () => settings?.PageData?.SiteSettings?.MarketTranslation,
    [settings],
  );
  let footerBlockFriendlyURls = useMemo(() => {
    return settings?.PageData?.SiteSettings?.FooterBlockFriendlyUrls;
  }, [settings]);
  let footerLinkFriendlyURls = useMemo(() => {
    return settings?.PageData?.SiteSettings?.FooterLinkFriendlyUrls;
  }, [settings]);

  const location = useLocation();
  const { pathname, search } = location;
  const queryString =
    process.env.NODE_ENV === ENV_VAR.DEVELOPMENT
      ? getSearchWithoutLocale(search)
      : search; // Only for local development purpose
  const data: any = useAppSelector(selectPage(pathname + queryString));
  let PageTypeNameValue: string = "";
  if (data) {
    if (data?.PageData !== null) {
      PageTypeNameValue = data?.PageData?.PageTypeName;
    }
  }
  const showMyAccountLinks = (PageTypeNameValue == "DebtOverviewPageData" ||
    PageTypeNameValue == "CustomerProfilePageData" ||
    PageTypeNameValue == "TransactionHistoryPageData" ||
    PageTypeNameValue == "DocumentsPageData") ? true : false;
  
    const hideTransctionHistoryLink = (user != null &&
      user.Status == CustomerStatus.LegalProcess) ? true : false;

  let MyAccountLinksFiltered = (hideTransctionHistoryLink ? (myAccountSubMenuLinks.filter((menulink: any) => menulink.pageTypeName != 'TransactionHistoryPageData')) : myAccountSubMenuLinks);
  if(!settings?.PageData?.SiteSettings?.EnableDocumentsTab || 
    settings?.LayoutModel?.NoOfDocuments == 0)
    {
      MyAccountLinksFiltered = MyAccountLinksFiltered.filter((menulink: any) => menulink.pageTypeName != 'DocumentsPageData')
    }
    if(settings?.LayoutModel?.HasViewDocumentsOnlyPermission)
    {
      MyAccountLinksFiltered = MyAccountLinksFiltered.filter((menulink: any) => menulink.pageTypeName != 'DebtOverviewPageData' && 
                                          menulink.pageTypeName != 'TransactionHistoryPageData')
    }
  let localPathToLoad = localePath;
  const Secured_PATH = 'secured';

  {
    (menuLinks || []).map((link: any,index:number) => {
      if (((link.url || '').includes(Secured_PATH))) {
          localPathToLoad = settings?.PageData?.SiteSettings?.PrivateManagementPageUrl?.Uri;
      }
    })
  };

  return (
    <UserContext.Provider value={user}>
      <Mobile>
        <MobileHeader
          links={menuLinks}
          localePath={localPathToLoad}
          appLinks={appLinks}
          registration={registration}
          instantPayment={instantPayment}
          translations={translations}
          registerLoginSettings={SiteSetingsRegisterLogin}
          showIncompleteDetailsAlert={showIncompleteDetailsAlert}
          hasViewDocumentsPermission = {settings?.LayoutModel?.HasViewDocumentsOnlyPermission}
        />
      </Mobile>
      <Desktop>
        <DesktopHeader
          links={menuLinks}
          localePath={localPathToLoad}
          appLinks={appLinks}
          registration={registration}
          translations={translations}
          registerLoginSettings={SiteSetingsRegisterLogin}
          showIncompleteDetailsAlert={showIncompleteDetailsAlert}
          hasViewDocumentsPermission = {settings?.LayoutModel?.HasViewDocumentsOnlyPermission}
        />
      </Desktop>
      {(user && showMyAccountLinks) &&
        (
          <AccountSubmenu
            links={MyAccountLinksFiltered}
          />
        )
      }
      {children}
      {footerBlockFriendlyURls != undefined && 
      <Footer settings={footerSettings} footerBlockFriendlyUrls={footerBlockFriendlyURls} footerLinkFriendlyUrls={footerLinkFriendlyURls} />
}
    </UserContext.Provider>
  );
};

export default BaseLayout;
